'use client';
import { shouldLoadGallerySlide } from './gallery-load-policy';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { useDeferredCarousel } from './use-deferred-carousel';
import { useCarouselAutoplay } from './use-carousel-autoplay';
import type { RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  ChevronDown as LuChevronDown,
  ChevronUp as LuChevronUp,
  Dot as LuDot,
  MoveLeft as LuMoveLeft,
  MoveRight as LuMoveRight,
} from 'lucide-react';

type CarouselClassName = {
  wrapper?: string;
  container?: string;
  slide?: string;
  arrows?: string;
  dots?: string;
  dot?: string;
  customDots?: string;
  customDot?: string;
  main?: string;
  thumbnail?: string;
  thumbnails?: string;
  thumbnailsWrapper?: string;
};

type CarouselCustomDots =
  | {
      showCustomDots: boolean;
      customDots: React.ReactNode[];
      colors: {
        activeColor: string;
        inactiveColor: string;
      };
    }
  | undefined;

export type CarouselProps = {
  className?: CarouselClassName;
  slides: React.ReactNode[];
  /** Small image nodes; defaults to slides for existing consumers. */
  thumbnailSlides?: React.ReactNode[];
  /** Product galleries only: leave unrelated carousel behavior unchanged. */
  lazySlides?: boolean;
  options?: EmblaOptionsType;
  customDots?: CarouselCustomDots;
  // styles
  showArrows?: boolean;
  showDots?: boolean;
  showThumbnails?: boolean;
  showThumbnailsArrows?: boolean;
  enableInterval?: boolean;
  intervalLength?: number;
  enableLoop?: boolean;
  openLightbox?: () => void;
  showMoreButton?: boolean;
  allImagesCount?: number;
  emblaRef?: RefObject<EmblaCarouselType>;
  /** Modra performance pilot: enhance the existing SSR slides after the first image paints. */
  deferUntilImagePaint?: boolean;
};

export function Carousel(props: CarouselProps) {
  const { t } = useTranslations();
  const [imagePainted, setImagePainted] = useState(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const { attach: emblaRef, api: emblaApi, activate, run } = useDeferredCarousel(
    { loop: !!props.enableLoop, ...props.options },
    !props.deferUntilImagePaint || imagePainted,
  );
  const attachViewport = useCallback((node: HTMLDivElement | null) => {
    viewportRef.current = node;
    emblaRef(node);
  }, [emblaRef]);

  useEffect(() => {
    if (!props.deferUntilImagePaint || imagePainted) return;
    const viewport = viewportRef.current;
    const image = viewport?.querySelector('img');
    let firstFrame = 0;
    let secondFrame = 0;
    const activate = () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => setImagePainted(true));
      });
    };
    if (!image || image.complete) activate();
    image?.addEventListener('load', activate);
    image?.addEventListener('error', activate);
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      image?.removeEventListener('load', activate);
      image?.removeEventListener('error', activate);
    };
  }, [props.deferUntilImagePaint, imagePainted]);
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  // Use a ref for dots; note: if you plan to use both default and custom dots concurrently,
  // consider using separate refs.
  const dotsRef = useRef<HTMLDivElement | null>(null);

  // Expose the embla API if provided.
  useImperativeHandle(props.emblaRef, () => emblaApi!, [emblaApi]);

  const carouselProviderContext = useCarouselContext();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [warmNeighbours, setWarmNeighbours] = useState(false);

  const onPrev = () => run(api => api.scrollPrev());
  const onNext = () => run(api => api.scrollNext());

  const isContextDefined = !!carouselProviderContext;

  useCarouselAutoplay(emblaApi, {
    enabled: !!props.enableInterval,
    intervalMs: props.intervalLength || 5000,
  });

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      if (!isContextDefined) return;
      carouselProviderContext?.setSelectedIndex(emblaApi.selectedScrollSnap());
      carouselProviderContext?.setCanGoNext(emblaApi.canScrollNext());
      carouselProviderContext?.setCanGoPrev(emblaApi.canScrollPrev());
    };

    if (isContextDefined) carouselProviderContext?.setApi(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      if (isContextDefined) carouselProviderContext?.setApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, isContextDefined]);

  useEffect(() => {
    if (!isContextDefined) return;
    carouselProviderContext?.setSlideCount(props.slides.length);
  }, [isContextDefined, props.slides.length]);

  const scrollThumbs = (delta: number) => {
    if (thumbsRef.current) {
      const container = thumbsRef.current;
      const currentScrollTop = container.scrollTop;
      container.scrollTo({
        top:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
            ? 0
            : currentScrollTop + delta,
        behavior: 'smooth',
      });
    }
  };

  const scrollDots = (delta: number) => {
    if (dotsRef.current) {
      const container = dotsRef.current;
      const currentScrollLeft = container.scrollLeft;
      container.scrollTo({
        left:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
            ? 0
            : currentScrollLeft + delta,
        behavior: 'smooth',
      });
    }
  };

  const slides = props.slides.map((slide, index) => {
    return (
      <div
        className={cn('h-full min-w-0 w-full flex-shrink-0', 'ea-carousel__slide', props.className?.slide)}
        key={index}
      >
        {!props.lazySlides || shouldLoadGallerySlide(index, selectedIndex, props.slides.length, warmNeighbours)
          ? slide
          : <><div className="h-full w-full bg-muted/20" /><noscript>{slide}</noscript></>}
      </div>
    );
  });

  const handleThumbClick = (index: number) => {
    const indexDelta = index - selectedIndex;
    run(api => api.scrollTo(index));
    scrollDots(indexDelta * 32);
  };

  const thumbs = props.showThumbnails && props.slides.map((slide, index) => {
    return (
      <div
        className={cn(
          'min-h-[48px] w-12 cursor-pointer rounded-sm bg-muted',
          'ea-carousel__thumb',
          index === selectedIndex ? 'border border-foreground' : '',
          props.className?.thumbnail,
        )}
        key={index}
        onClick={() => handleThumbClick(index)}
      >
        {props.thumbnailSlides?.[index] ?? slide}
      </div>
    );
  });

  return (
    <div className={cn('flex min-w-0 w-full gap-1', 'ea-carousel__wrapper', props.className?.wrapper)}>
      {props.showThumbnails && (
        <div
          className={cn(
            'hidden h-full max-h-[500px] min-w-[56px] max-w-[56px] flex-col gap-2 md:flex',
            'ea-carousel__thumbnails',
            props.className?.thumbnailsWrapper,
          )}
        >
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-up',
              )}
              variant="secondary"
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              onClick={() => {
                onPrev();
                scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              <LuChevronUp size={16} />
            </Button>
          )}
          <div
            ref={thumbsRef}
            className={cn(
              'hide-scroll-bar flex min-w-[56px] max-w-[56px] flex-col items-center justify-start gap-2 overflow-hidden rounded-sm',
              'ea-carousel__thumbnail',
              props.className?.thumbnails,
            )}
          >
            {thumbs}
            {props.showMoreButton && (
              <div
                className={cn(
                  'flex min-h-[48px] w-12 cursor-pointer items-center justify-center rounded-lg bg-muted',
                  'ea-carousel__thumb',
                  props.className?.thumbnail,
                  'bg-primary text-background',
                )}
                onClick={() => props.openLightbox?.()}
              >
                {props.allImagesCount ? `+${props.allImagesCount - 4}` : 'Zobraziť viac'}
              </div>
            )}
          </div>
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-down',
              )}
              variant="secondary"
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              onClick={() => {
                onNext();
                scrollThumbs(56);
                scrollDots(32);
              }}
            >
              <LuChevronDown size={16} />
            </Button>
          )}
        </div>
      )}
      <div
        className={cn('relative min-w-0 overflow-hidden', 'ea-carousel', props.className?.main)}
        ref={attachViewport}
        data-carousel-ready={!!emblaApi}
        onPointerDownCapture={() => { if (props.lazySlides) setWarmNeighbours(true); activate(); }}
        onFocusCapture={() => { if (props.lazySlides) setWarmNeighbours(true); activate(); }}
      >
        <div
          className={cn('flex h-full ', 'ea-carousel__container', props.className?.container)}
          style={{ touchAction: props.options?.axis === 'y' ? 'pan-x pinch-zoom' : 'pan-y pinch-zoom' }}
          onDragStartCapture={event => {
            // Keep native image/link dragging from fighting Embla's pointer handling.
            if ((event.target as HTMLElement).closest('img')) event.preventDefault();
          }}
          onClick={props.openLightbox}
        >
          {slides}
        </div>
        {props.showArrows && (
          <>
            <button
              aria-label={t('text3525')}
              className={cn(
                'absolute left-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--left',
                props.className?.arrows,
              )}
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              onClick={() => {
                onPrev();
                scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              <LuMoveLeft className="h-4 w-4" />
            </button>
            <button
              aria-label={t('text3526')}
              className={cn(
                'absolute right-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--right',
                props.className?.arrows,
              )}
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              onClick={() => {
                onNext();
                scrollThumbs(56);
                scrollDots(32);
              }}
            >
              <LuMoveRight className="h-4 w-4" />
            </button>
          </>
        )}
        {props.showDots && (
          <div
            className={cn(
              'absolute bottom-4 flex w-full items-center justify-center',
              'ea-carousel__dots__container',
              props.className?.dots,
            )}
          >
            <div
              className={cn(
                'flex max-w-[160px] cursor-pointer justify-start overflow-hidden',
                'ea-carousel__dots__wrapper',
              )}
              ref={dotsRef}
            >
              {slides.map((_, index) => (
                <LuDot
                  key={index}
                  className={cn(
                    `h-10 w-10 ${
                      index === selectedIndex ? 'text-foreground' : 'text-muted-foreground'
                    }`,
                    'ea-carousel__dot',
                    props.className?.dot,
                  )}
                  onClick={() => run(api => api.scrollTo(index))}
                />
              ))}
            </div>
          </div>
        )}
        {props.customDots?.showCustomDots && (
          <div
            className={cn(
              'flex w-full items-center justify-center',
              'ea-carousel__custom-dots__container',
              props.className?.customDots,
            )}
          >
            <div
              className={cn('flex w-full cursor-pointer', 'ea-carousel__custom-dots__wrapper')}
              ref={dotsRef}
            >
              {props.customDots.customDots.map((dot, index) => (
                <div
                  key={index}
                  className={cn(
                    `h-full w-full transition-all duration-500 ${
                      index === selectedIndex
                        ? props.customDots?.colors.activeColor
                        : props.customDots?.colors.inactiveColor
                    }`,
                    props.className?.customDot,
                    'ea-carousel__custom-dot',
                  )}
                  onClick={() => run(api => api.scrollTo(index))}
                >
                  <div
                    className={cn(
                      'absolute left-1/2 top-[-8px] border-solid',
                      'ea-carousel__custom-dot-extra',
                    )}
                    style={{
                      content: '""',
                      transform: 'translateX(-50%)',
                      borderWidth: '0 5px 8px 5px',
                      borderColor:
                        index === selectedIndex ? 'hsl(var(--accent))' : 'hsl(var(--secondary))',
                    }}
                  />
                  {dot}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
